/**
 * BrainMemoriesStore — web mirror of `Sources/Store/BrainMemoriesStore.swift`
 * (the LIVE path).
 *
 * The real memories client. Talks directly to the Brain Worker's read-only
 * memories REST API (docs/MEMORIES_API.md), NOT Supabase/PostgREST — memories are
 * served by the Worker and scoped by the caller's workspace memberships.
 *
 * AUTH: every request carries `Authorization: Bearer brain_*` — the SAME durable
 * token BrainAuth holds in localStorage (it does NOT use the short-lived Supabase
 * JWT). Constructed with a `brainToken` provider (supplied by BrainAuth) so it
 * never learns how the token is stored or refreshed.
 */

import type { BrainConfig } from '../config';
import {
  decodeMemory,
  decodeMemorySummary,
  type Memory,
  type MemoriesListResponse,
  type MemoryDetailResponse,
  type MemorySummary,
  type MemoryType,
} from '../models/memory';
import { friendlyMemoriesError, MemoriesStoreError, type MemoriesStore } from './MemoriesStore';

export class BrainMemoriesStore implements MemoriesStore {
  private readonly baseURL: string;
  private readonly brainToken: () => Promise<string | null>;

  /**
   * @param config      provides the Brain Worker base URL.
   * @param brainToken  returns the current `brain_*` bearer token (from BrainAuth).
   */
  constructor(config: BrainConfig, brainToken: () => Promise<string | null>) {
    this.baseURL = config.brainBaseURL;
    this.brainToken = brainToken;
  }

  async list(opts?: { workspace?: string; type?: MemoryType; limit?: number }): Promise<MemorySummary[]> {
    const params = new URLSearchParams();
    if (opts?.workspace) params.set('workspace', opts.workspace);
    if (opts?.type) params.set('type', opts.type);
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    const payload = await this.request<MemoriesListResponse>('/memories', params);
    return payload.memories.map(decodeMemorySummary);
  }

  async get(id: string): Promise<Memory> {
    const payload = await this.request<MemoryDetailResponse>(`/memories/${encodeURIComponent(id)}`);
    return decodeMemory(payload.memory);
  }

  async search(opts: {
    query: string;
    workspace?: string;
    type?: MemoryType;
    limit?: number;
  }): Promise<MemorySummary[]> {
    const params = new URLSearchParams({ q: opts.query });
    if (opts.workspace) params.set('workspace', opts.workspace);
    if (opts.type) params.set('type', opts.type);
    if (opts.limit != null) params.set('limit', String(opts.limit));
    const payload = await this.request<MemoriesListResponse>('/memories/search', params);
    return payload.memories.map(decodeMemorySummary);
  }

  // MARK: - Internals

  private async request<T>(path: string, params?: URLSearchParams): Promise<T> {
    const token = await this.brainToken();
    if (!token) throw new MemoriesStoreError('No Brain token', 401);

    const url = new URL(path, this.baseURL);
    if (params) url.search = params.toString();

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    const text = await res.text();
    if (!res.ok) {
      throw new MemoriesStoreError(friendlyMemoriesError(res.status, text), res.status);
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new MemoriesStoreError('Unexpected Brain response: not JSON');
    }
  }
}
